import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { EnvVars } from '../../constants/EnvConfig';
import { GOOGLE_API_PROXY_URL } from '../../utils/geocoding';
import Colors from '../../constants/Colors';
import { StyleSheet, View, TouchableOpacity, Keyboard } from 'react-native';
import { logError } from '../../utils/logger';
import useGooglePlacesAutocomplete from './useGooglePlacesAutocomplete';
import { AppText } from '../AppText';
import { AppTextInput } from '../AppTextInput';

function AddressSelector(props) {
  const {
    translation,
    onSelect,
    addressStreet1,
    onChange,
    state,
    city,
    handleForceUpdate,
    isRequired,
    testID,
  } = props;
  const { googlePlacesApiKey } = EnvVars;
  const [inputValue, setInputValue] = useState(addressStreet1 || '');
  const [fields, setFields] = useState(null);
  const [didForceUpdate, setDidForceUpdate] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const blurTimeoutRef = useRef(null);

  const { suggestions, fetchSuggestions, fetchPlaceDetails, clearSuggestions } = useGooglePlacesAutocomplete({
    apiKey: googlePlacesApiKey,
    proxyUrl: GOOGLE_API_PROXY_URL,
  });

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange(inputValue);
    }
  }, [inputValue]);

  const onSelectPlace = (addressObject) => {
    if (!addressObject) {
      logError(new Error('Places API returned null details'));
      return;
    }

    const user = {};
    const updateUser = (field, value) => {
      user[field] = value;
    };
    // New Places API returns addressComponents (camelCase) with shortText/longText
    // Old API returned address_components (snake_case) with short_name/long_name
    const components = addressObject.addressComponents || addressObject.address_components;
    const getName = (component) =>
      component.shortText || component.short_name || component.longText || component.long_name;

    if (components && components.length > 0 && getName(components[0])) {
      let streetName;
      let streetNumber;
      components.forEach((component) => {
        const shortName = getName(component);
        if (component.types.indexOf('route') !== -1) {
          streetName = shortName;
        } else if (component.types.indexOf('street_number') !== -1) {
          streetNumber = shortName;
        } else if (component.types.indexOf('locality') !== -1) {
          updateUser('city', shortName);
        } else if (component.types.indexOf('sublocality') !== -1) {
          updateUser('city', shortName);
        } else if (component.types.indexOf('administrative_area_level_1') !== -1) {
          updateUser('state', shortName);
        } else if (component.types.indexOf('floor') !== -1) {
          // floor is set as address_street_1 but intentionally overwritten below by street number + route when present
          updateUser('address_street_1', shortName);
        } else if (component.types.indexOf('postal_code') !== -1) {
          updateUser('zip_code', shortName);
        }
      });
      if (streetName || streetNumber) {
        const street = [];
        if (streetNumber) street.push(streetNumber);
        if (streetName) street.push(streetName);
        updateUser('address_street_1', street.join(' '));
        setInputValue(street.join(' '));
      }
    } else {
      logError(new Error('Places API returned an abnormal payload'), {
        componentCount: components?.length ?? 0,
        placeId: addressObject?.id ?? addressObject?.place_id ?? null,
      });
      return;
    }
    updateUser('google_place_id', addressObject?.id || addressObject?.place_id || null);
    setFields(user);
    onSelect(user);
    handleForceUpdate();
  };

  // There is a rare case where when the fields are empty the parent component doesn't update
  if (!state && !city && fields) {
    if (didForceUpdate) return;
    onSelect(fields);
    setDidForceUpdate(true);
    handleForceUpdate();
  }

  const handleChangeText = (text) => {
    setInputValue(text);
    fetchSuggestions(text);
    setShowDropdown(true);
  };

  const handleSelectSuggestion = async (item) => {
    setShowDropdown(false);
    clearSuggestions();
    Keyboard.dismiss();
    const details = await fetchPlaceDetails(item.placeId);
    onSelectPlace(details);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    // Delay hiding dropdown so touch on suggestion can register first
    blurTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  };

  return (
    <View style={styles.container}>
      <AppText style={styles.label}>{translation.address_street_1}</AppText>
      <View style={styles.autocompleteContainer}>
        <AppTextInput
          style={styles.textInput}
          placeholder={translation.address_autocomplete_placeholder}
          placeholderTextColor={Colors.mediumGray}
          value={inputValue}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          testID={testID}
        />
        {showDropdown && suggestions.length > 0 && (
          <View style={styles.listView} testID="suggestions-list">
            {suggestions.map((item) => (
              <TouchableOpacity
                key={item.placeId}
                style={styles.row}
                onPress={() => handleSelectSuggestion(item)}
                testID={`suggestion-${item.placeId}`}
              >
                <AppText style={styles.rowText} numberOfLines={1}>
                  {item.description}
                </AppText>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
      {isRequired && (
        <View style={styles.requiredContainer}>
          <AppText style={[styles.requiredTextStyle, styles.requiredHelperStyle]}>
            {translation.form_required}
          </AppText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: 70,
  },
  autocompleteContainer: {
    zIndex: 1,
    marginTop: 2,
  },
  textInput: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.grayBorder,
    paddingHorizontal: 0,
    paddingVertical: 0,
    minHeight: 24,
    fontSize: 16,
  },
  listView: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: Colors.lightBackground,
    zIndex: 2,
  },
  row: {
    backgroundColor: Colors.lightBackground,
    maxHeight: 32,
    minHeight: 32,
    padding: 0,
    margin: 0,
    height: 32,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  rowText: {
    fontSize: 14,
  },
  requiredContainer: {
    flexDirection: 'row',
    marginTop: -30,
  },
  requiredTextStyle: {
    color: Colors.brightRed,
    fontWeight: 'bold',
  },
  requiredHelperStyle: {
    fontSize: 10,
  },
  label: {
    color: Colors.veryDarkGray,
  },
});

AddressSelector.propTypes = {
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  addressStreet1: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AddressSelector;
